import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { addDays, localMidnightIso, todayIn } from '@frisk/shared';
import { PATHS } from '../../navigation';
import { t } from '../../theme';
import { useStore, useWasteCollections } from '../../store';
import { Widget, WidgetEmpty } from './Widget';
import { WasteIcon } from './WasteIcon';

/**
 * When to put the bins out.
 *
 * The actionable moment is the evening before — by the morning the lorry has
 * been — so the tile leads with tomorrow whenever there is a tomorrow, and
 * falls back to the next dated collection when there is not.
 *
 * No notifications involved: the panel is already lit and already on the wall,
 * which is the entire delivery mechanism.
 */
export function WasteWidget() {
  const loadEvents = useStore((s) => s.loadEvents);
  const navigate = useNavigate();
  const days = useWasteCollections();

  const today = todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const tomorrow = addDays(today, 1);

  useEffect(() => {
    void loadEvents(localMidnightIso(today), localMidnightIso(addDays(today, 28)));
  }, [loadEvents, today]);

  const upcoming = days.filter((d) => d.date >= today);
  const next = upcoming[0];
  const isTomorrow = next?.date === tomorrow;
  const isToday = next?.date === today;

  const dayLabel = (date: string) =>
    date === today
      ? 'Heute'
      : date === tomorrow
        ? 'Morgen'
        : new Date(`${date}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <Widget kicker="MÜLL" onTap={() => navigate(PATHS.cal)}>
      {!next ? (
        <WidgetEmpty>Keine Abfuhrtermine.</WidgetEmpty>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          {/* The lead line: big when it is tonight's job, plain otherwise. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ color: next.kinds[0]!.colour, display: 'flex', flex: 'none' }}>
              <WasteIcon size={isTomorrow || isToday ? 40 : 30} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: isTomorrow || isToday ? 30 : 24,
                  fontWeight: 600,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  color: isTomorrow || isToday ? t.ink : t.inkMuted,
                }}
              >
                {dayLabel(next.date)}
              </div>
              <div style={{ fontSize: 17, color: t.inkMuted, marginTop: 3, textWrap: 'pretty' }}>
                {next.kinds.map((k) => k.label).join(' · ')}
              </div>
            </div>
          </div>

          {upcoming.slice(1, 4).map((d) => (
            <div key={d.date} style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 3, flex: 'none' }}>
                {d.kinds.map((k) => (
                  <div key={k.type} style={{ width: 8, height: 8, borderRadius: 999, background: k.colour }} />
                ))}
              </div>
              <div style={{ fontSize: 15, color: t.inkFaint, flex: 'none' }}>{dayLabel(d.date)}</div>
              <div
                style={{
                  fontSize: 15,
                  color: 'oklch(0.72 0.015 70)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                {d.kinds.map((k) => k.label).join(' · ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </Widget>
  );
}
